<script>
  // This component includes code in the root HTML file that loads this component to override the default Google Places autocomplete styles set on shadow root elements

  import { onMount, createEventDispatcher, tick } from 'svelte';
  import ct from 'countries-and-timezones';
  import {normalizeGeos} from '@utils/helpers';

  let inputElement;
  let autocomplete;

  const dispatch = createEventDispatcher();

  export let apiKey = process.env.GOOGLE_MAPS;
  export let placeholder = 'Enter a location';
  export let country = 'US';
  export let error = [];
  export let worldwide;
  export let backgroundColor = 'transparent';
  export let textColor = '#000';
  export let focusColor = '#1c64f2';
  export let colorScheme = '';
  /** When set, the address is confirmed with Continue instead of as soon as the fields are filled. */
  export let showContinue = false;
  export let continuing = false;
  /** Continue spans the form. Pass false to keep a short button beside the summary. */
  export let continueFullWidth = true;
  /** Hides the form and shows the confirmed address with Edit. */
  export let collapsed = false;

  export let countries = worldwide ? normalizeGeos(Object.values(ct.getAllCountries()), ['US', 'CA', 'GB']) : [{value: 'US', label: 'United States'}, {value: 'CA', label: 'Canada'}];

  const FIELD_ERRORS = ['street', 'line1', 'city', 'state', 'zip', 'postal_code', 'postal code', 'postal'];

  function blankAddress() {
    return {
      line1: '',
      apartment: '',
      city: '',
      state: '',
      postal_code: '',
      country: country || '',
    };
  }

  let selected = blankAddress();
  let editingManually = false;

  onMount(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.onload = () => initAutocomplete();
      document.body.appendChild(script);
    } else {
      initAutocomplete();
    }
  });

  // Helper function to inject styles into shadow DOM
  function applyShadowStyles(color, focusBorderColor) {
    if (!autocomplete) return;

    const injectStyle = () => {
      if (autocomplete.shadowRoot) {
        let style = autocomplete.shadowRoot.querySelector('style[data-custom-styles]');
        if (!style) {
          style = document.createElement('style');
          style.setAttribute('data-custom-styles', 'true');
          autocomplete.shadowRoot.appendChild(style);
        }
        style.textContent = `
          input {
            color: ${color} !important;
          }
          input:focus {
            outline: none !important;
          }
          .input-container:focus-within {
            border-color: ${focusBorderColor} !important;
            box-shadow: 0 0 0 2px ${focusBorderColor}33 !important;
          }
        `;

        const input = autocomplete.shadowRoot.querySelector('input');
        if (input) {
          input.style.color = color;
        }

        if (input && !input.dataset.focusListenerAdded) {
          input.dataset.focusListenerAdded = 'true';
          input.addEventListener('focus', () => {
            autocomplete.style.setProperty('border-color', focusBorderColor);
            autocomplete.style.setProperty('box-shadow', `0 0 0 2px ${focusBorderColor}33`);
          });
          input.addEventListener('blur', () => {
            autocomplete.style.setProperty('border-color', lineError ? '#dc2626' : '#d7d9dd');
            autocomplete.style.setProperty('box-shadow', 'none');
          });
        }

        return true;
      }
      return false;
    };

    if (!injectStyle()) {
      const observer = new MutationObserver(() => {
        if (injectStyle()) {
          observer.disconnect();
        }
      });
      observer.observe(autocomplete, { childList: true, subtree: true });

      setTimeout(() => {
        injectStyle();
        observer.disconnect();
      }, 100);
      setTimeout(() => {
        injectStyle();
        observer.disconnect();
      }, 500);
    }
  }

  $: if (autocomplete && (textColor || focusColor)) {
    applyShadowStyles(textColor, focusColor);
  }

  $: if (autocomplete) {
    autocomplete.style.setProperty('border-color', lineError ? '#dc2626' : '#d7d9dd');
  }

  function initAutocomplete() {
    if (!inputElement || editingManually) return;

    const existingAutocomplete = inputElement.querySelector('gmp-place-autocomplete');
    if (existingAutocomplete) {
      existingAutocomplete.remove();
    }

    autocomplete = new google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: !worldwide ? Object.values(countries).map(c => c.value.toLowerCase()) : [],
    });
    autocomplete.placeholder = placeholder;
    inputElement.appendChild(autocomplete);

    autocomplete.addEventListener('gmp-select', handlePlaceChanged);
    autocomplete.style.setProperty('background-color', backgroundColor);
    autocomplete.style.setProperty('width', '100%');
    autocomplete.style.setProperty('height', '42px');
    autocomplete.style.setProperty('border-radius', '6px');
    autocomplete.style.setProperty('border', 'solid 1px #d7d9dd');
    autocomplete.style.setProperty('color', textColor);

    if (colorScheme) {
      autocomplete.style.colorScheme = colorScheme;
    }

    if (textColor || focusColor) {
      applyShadowStyles(textColor, focusColor);
    }

    autocomplete.addEventListener('click', handleClearButtonClick);
    autocomplete.style.setProperty('display', 'block');
    autocomplete.style.setProperty('box-sizing', 'border-box');
  }

  function handleClearButtonClick(event) {
    const rect = autocomplete.getBoundingClientRect();
    const clickX = event.clientX;
    const clearButtonZone = rect.right - 40;

    if (clickX >= clearButtonZone && (selected.line1 || selected.city)) {
      handleInputCleared();
    }
  }

  function handleInputCleared() {
    selected = blankAddress();
    dispatch('clear');
    dispatch('select', null);
  }

  function textOf(value) {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    return (value.text || '').trim();
  }

  function componentText(component) {
    return component?.longText || component?.shortText || '';
  }

  // The widget often shows "de los cholanes 119, City". The street is the first segment.
  function streetFromVisibleText(raw, city) {
    let text = (raw || '').trim();
    if (!text) return '';
    const locality = (city || '').trim();
    if (locality) {
      const at = text.toLowerCase().indexOf(locality.toLowerCase());
      if (at > 0) text = text.slice(0, at);
    }
    return text.split(',')[0].trim();
  }

  function line1FromPlace(parsed, placePrediction) {
    const number = (parsed.street_number || '').trim();
    const route = (parsed.route || '').trim();
    const rebuilt = [number, route].filter(Boolean).join(' ');
    const seen = streetFromVisibleText(
      textOf(placePrediction?.mainText) || textOf(placePrediction?.text),
      parsed.city,
    );
    if (!seen) return rebuilt;
    const seenLower = seen.toLowerCase();
    const routeKept = !route || seenLower.includes(route.toLowerCase());
    const numberKept = !number || seenLower.includes(number.toLowerCase());
    // Keep the text the user saw when it still has the building number.
    // Rebuilding as "number + street" drops a trailing number Google did not split out.
    if (routeKept && numberKept && (number || /\d/.test(seen))) return seen;
    return rebuilt || seen;
  }

  function captureTypedStreet() {
    const input = autocomplete?.shadowRoot?.querySelector('input');
    const street = streetFromVisibleText(input?.value, selected.city);
    if (!street) return;
    const current = (selected.line1 || '').trim();
    const missingNumber = (street.match(/\d+/g) || []).some(n => !current.includes(n));
    if (!current || missingNumber) selected.line1 = street;
  }

  async function handlePlaceChanged({ placePrediction }) {
    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents'] });
    const parsed = parseAddress(place);
    const apartment = parsed.apartment || selected.apartment || '';
    selected = {
      line1: line1FromPlace(parsed, placePrediction),
      apartment,
      city: parsed.city || '',
      state: parsed.state || '',
      postal_code: parsed.postal_code || '',
      country: parsed.country || country,
    };
    if (parsed.country) country = parsed.country;
    emitAddress();
  }

  function parseAddress(place) {
    error = [];
    const components = {};
    if (place) {
      place.addressComponents?.forEach(component => {
        const types = component.types;

        if (types.includes('street_number')) components.street_number = componentText(component);
        if (types.includes('route')) components.route = componentText(component);
        if (types.includes('locality')) components.city = componentText(component);
        if (types.includes('administrative_area_level_1')) components.state = component.shortText || component.longText || '';
        if (types.includes('country')) components.country = component.shortText || component.longText || '';
        if (types.includes('postal_code')) components.postal_code = componentText(component);
        if (types.includes('subpremise')) components.subpremise = componentText(component);
        if (!components.city && types.includes('sublocality')) {
          components.city = componentText(component);
        }
        if (!components.city && types.includes('postal_town')) {
          components.city = componentText(component);
        }
        if (!components.state && types.includes('administrative_area_level_2')) {
          components.state = component.shortText || component.longText || '';
        }
        if (!components.route && (types.includes('sublocality_level_4') || types.includes('sublocality_level_5'))) {
          components.route = componentText(component);
        }
        if (!components.street_number && types.includes('premise')) components.street_number = componentText(component);
      });

      components.line1 = [components.street_number, components.route].filter(Boolean).join(' ');
      components.apartment = components.subpremise || '';
    }
    return components;
  }

  async function updateCountry() {
    editingManually = false;
    selected = blankAddress();
    if (Array.isArray(error) && error.length) {
      error = error.filter(entry => {
        const key = String(entry).toLowerCase();
        return !FIELD_ERRORS.includes(key) && key !== 'address';
      });
    }
    dispatch('clear');
    dispatch('select', null);
    await tick();
    if (window.google?.maps?.places) initAutocomplete();
  }

  function combinedLine1(street, apartment) {
    const line = (street || '').trim();
    const unit = (apartment || '').trim();
    if (line && unit) return `${line}, ${unit}`;
    return line;
  }

  function addressPayload() {
    return {
      line1: combinedLine1(selected.line1, selected.apartment),
      city: selected.city || '',
      state: selected.state || '',
      postal_code: selected.postal_code || '',
      country: selected.country || country,
    };
  }

  export function getValue() {
    return addressPayload();
  }

  function validateManualAddress() {
    const missing = [];
    if (!(selected.line1 || '').trim()) missing.push('line1');
    if (!(selected.city || '').trim()) missing.push('city');
    if (!(selected.state || '').trim()) missing.push('state');
    if (!(selected.postal_code || '').trim()) missing.push('postal_code');
    return missing;
  }

  function syncFieldErrors() {
    if (!Array.isArray(error) || !error.length) return;
    const missing = new Set(validateManualAddress());
    const next = error.filter(entry => {
      const key = String(entry).toLowerCase();
      if (['street', 'line1', 'address'].includes(key)) return missing.has('line1');
      if (key === 'city') return missing.has('city');
      if (key === 'state') return missing.has('state');
      if (['zip', 'postal_code', 'postal code', 'postal'].includes(key)) return missing.has('postal_code');
      return true;
    });
    if (next.length !== error.length) error = next;
  }

  async function emitAddress() {
    await tick();
    syncFieldErrors();
    const payload = addressPayload();
    if (!payload.line1 && !payload.city && !payload.state && !payload.postal_code) return;
    dispatch('select', payload);
  }

  function editAddress() {
    dispatch('edit');
  }

  function confirmAddress() {
    captureTypedStreet();
    const payload = addressPayload();
    const missing = validateManualAddress();
    if (missing.length || !payload.country) {
      error = [
        ...(missing.includes('line1') ? ['address'] : []),
        ...(missing.includes('city') ? ['city'] : []),
        ...(missing.includes('state') ? ['state'] : []),
        ...(missing.includes('postal_code') ? ['postal code'] : []),
        ...(!payload.country ? ['country'] : []),
      ];
      editingManually = true;
      return;
    }
    error = [];
    dispatch('continue', payload);
  }

  function enterManually() {
    captureTypedStreet();
    editingManually = true;
    emitAddress();
  }

  async function backToSearch() {
    const street = (selected.line1 || '').trim();
    editingManually = false;
    await tick();
    if (window.google?.maps?.places) initAutocomplete();
    const showStreet = () => {
      const input = autocomplete?.shadowRoot?.querySelector('input');
      if (input && street) input.value = street;
    };
    showStreet();
    setTimeout(showStreet, 100);
  }

  $: stateLabel = country === 'CA' ? 'Province' : 'State';
  $: postalLabel = country === 'CA' ? 'Postal code' : 'ZIP / Postal';
  $: locality = [selected.city, `${selected.state || ''} ${selected.postal_code || ''}`.trim()].filter(Boolean).join(' ');
  $: summary = [combinedLine1(selected.line1, selected.apartment), locality].filter(Boolean).join(', ');

  $: errorList = Array.isArray(error) ? error.map(e => String(e).toLowerCase()) : [];
  $: lineError = errorList.some(e => ['street', 'line1', 'address'].includes(e));
  $: cityError = errorList.includes('city');
  $: stateError = errorList.includes('state');
  $: zipError = errorList.some(e => ['zip', 'postal_code', 'postal code', 'postal'].includes(e));

  // A missing street, city, state, or postal code has to be typed in, so open
  // the same fields for editing instead of leaving them read-only.
  $: if (errorList.some(e => FIELD_ERRORS.includes(e))) {
    editingManually = true;
  }
</script>

<style>
  .address-form {
    width: 100%;
  }

  .field {
    margin-bottom: 1rem;
  }

  .field label,
  .locality label,
  .apartment label {
    display: block;
    margin-bottom: 0.25rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  .text-input,
  select {
    width: 100%;
    height: 42px;
    padding: 0.5rem 0.75rem;
    font-size: 1rem;
    border: 1px solid #d7d9dd;
    border-radius: 6px;
    background-color: #fff;
    color: #111827;
  }

  select {
    appearance: none;
    -webkit-appearance: none;
    padding-right: 2.25rem;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 1rem;
  }

  .text-input:focus,
  select:focus {
    outline: none;
    border-color: #1c64f2;
    box-shadow: 0 0 0 2px #1c64f233;
  }

  .text-input.invalid,
  .address-error {
    border-color: #dc2626 !important;
  }

  .place-autocomplete-card {
    width: 100%;
  }

  .hint {
    margin-top: 0.35rem;
    text-align: left;
    font-size: 0.8rem;
    color: #6b7280;
  }

  .manual-link {
    background: none;
    border: 0;
    padding: 0;
    font-size: inherit;
    color: #1c64f2;
    cursor: pointer;
    text-decoration: underline;
  }

  .apartment {
    margin-bottom: 1rem;
    text-align: left;
  }

  .apartment-help {
    margin-top: 0.35rem;
    font-size: 0.8rem;
    color: #6b7280;
  }

  .locality {
    display: flex;
    gap: 0.75rem;
    text-align: left;
  }

  .locality > div {
    flex: 1;
    min-width: 0;
  }

  .locality .state {
    flex: 0.7;
  }

  .field-error {
    margin-top: 0.2rem;
    font-size: 0.75rem;
    color: #dc2626;
    text-align: left;
  }

  .address-confirmed {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #f9fafb;
  }

  .address-confirmed p {
    margin: 0;
    min-width: 0;
    text-align: left;
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
    overflow-wrap: anywhere;
  }

  .edit-btn {
    flex: none;
    background: none;
    border: 0;
    padding: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--secondary-color, #2196c9);
    cursor: pointer;
  }

  .edit-btn:hover:not(:disabled) {
    text-decoration: underline;
  }

  .edit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .address-fields.is-collapsed {
    display: none;
  }

  .shipping-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 0.9rem;
    border-top: 1px solid #e5e7eb;
  }

  .shipping-footer.full-width {
    flex-direction: column;
    align-items: stretch;
  }

  .shipping-summary {
    margin: 0;
    text-align: left;
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
  }

  .continue-btn {
    flex: none;
    margin-left: auto;
    background: var(--secondary-color);
    color: var(--over-secondary-color);
    border: 0;
    border-radius: 8px;
    padding: 0.7rem 1.35rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
  }

  .continue-btn.full-width {
    width: 100%;
    margin-left: 0;
    border-radius: 6px;
    padding: 0.75rem 1rem;
  }

  .continue-btn:hover:not(:disabled) {
    background: var(--secondary-dark, var(--secondary-color));
  }

  .continue-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media screen and (max-width: 640px) {
    .locality {
      flex-wrap: wrap;
    }

    .locality > div {
      flex: 1 1 30%;
    }

    .shipping-footer {
      align-items: flex-start;
      flex-direction: column;
    }

    .continue-btn {
      margin-left: 0;
    }
  }
</style>

<div class="address-form {$$props.class || ''}">
  {#if collapsed}
    <div class="address-confirmed">
      <p>{summary}</p>
      <button type="button" class="edit-btn" disabled={continuing} on:click={editAddress}>Edit</button>
    </div>
  {/if}
  <div class="address-fields" class:is-collapsed={collapsed}>
  <div class="field">
    <label for="place-country">Country</label>
    <select id="place-country" name="country" bind:value={country} on:change={updateCountry}>
      {#each countries as geo}
        <option value={geo.value}>{geo.label}</option>
      {/each}
    </select>
  </div>

  <div class="field">
    <label for="place-address">Street address</label>
    {#if editingManually}
      <input
        id="place-address"
        type="text"
        class="text-input"
        class:invalid={lineError}
        bind:value={selected.line1}
        on:input={emitAddress}
        placeholder={placeholder}
      />
    {:else}
      <div class="place-autocomplete-card" class:address-error={lineError} id="place-autocomplete-card" bind:this={inputElement}></div>
    {/if}
    {#if lineError}
      <p class="field-error">Street address is required</p>
    {/if}
    <p class="hint">
      {#if editingManually}
        <button type="button" class="manual-link" on:click={backToSearch}>Back to search</button>
      {:else}
        Start typing and pick your address from the list.
        <button type="button" class="manual-link" on:click={enterManually}>Can't find it? Enter it manually</button>
      {/if}
    </p>
  </div>

  <div class="apartment">
    <label for="place-apartment">Apartment, suite or unit (optional)</label>
    <input
      id="place-apartment"
      type="text"
      class="text-input"
      bind:value={selected.apartment}
      on:input={emitAddress}
      placeholder="e.g. Apt 4B, Unit 12, Suite 300"
    />
    <p class="apartment-help">Live in an apartment or condo? Add your unit number here so your package reaches your door.</p>
  </div>

  <div class="locality">
    <div>
      <label for="place-city">City</label>
      <input
        id="place-city"
        type="text"
        class="text-input"
        class:invalid={cityError}
        bind:value={selected.city}
        on:input={emitAddress}
      />
      {#if cityError}
        <p class="field-error">City is required</p>
      {/if}
    </div>
    <div class="state">
      <label for="place-state">{stateLabel}</label>
      <input
        id="place-state"
        type="text"
        class="text-input"
        class:invalid={stateError}
        bind:value={selected.state}
        on:input={emitAddress}
      />
      {#if stateError}
        <p class="field-error">{stateLabel} is required</p>
      {/if}
    </div>
    <div>
      <label for="place-postal">{postalLabel}</label>
      <input
        id="place-postal"
        type="text"
        class="text-input"
        class:invalid={zipError}
        bind:value={selected.postal_code}
        on:input={emitAddress}
        placeholder={country === 'CA' ? 'Postal code' : 'ZIP'}
      />
      {#if zipError}
        <p class="field-error">{postalLabel} is required</p>
      {/if}
    </div>
  </div>

  {#if summary || showContinue}
    <div class="shipping-footer" class:full-width={continueFullWidth && showContinue}>
      {#if summary}
        <p class="shipping-summary">Shipping to: {summary}</p>
      {/if}
      {#if showContinue}
        <button type="button" class="continue-btn" class:full-width={continueFullWidth} disabled={continuing} on:click={confirmAddress}>Continue</button>
      {/if}
    </div>
  {/if}
  </div>
</div>
